import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { campaignRenameItems, type CampaignRenameResult } from "@shared/ads-manager-rename";

export function CampaignRenameDialog({ brandId, accountId, campaigns, onClose, onAcknowledged, onPending, basePath, requestContext }: {
  brandId: string; accountId: string; campaigns: Array<{ id: string; name: string }>;
  onClose: () => void; onAcknowledged: (results: CampaignRenameResult[]) => void;
  onPending: (pending: boolean) => void;
  basePath?: string;
  requestContext?: { accountRowId: string; pageId: string };
}) {
  const [names, setNames] = useState(() => campaigns.map(c => c.name));
  const [review, setReview] = useState(false);
  const [pending, setPending] = useState(false);
  const [results, setResults] = useState<CampaignRenameResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const flight = useRef(false), mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const items = campaigns.map((campaign, i) => ({ campaignId: campaign.id, newName: names[i] }));
  let valid = true;
  try { campaignRenameItems(items); } catch { valid = false; }
  const submit = async () => {
    if (flight.current || !valid || !review || results) return;
    flight.current = true; setPending(true); onPending(true); setError(null);
    try {
      const bulk = campaigns.length > 1;
      const response = await fetch(`${basePath ?? `/api/brands/${brandId}/ads-manager/campaigns`}/${bulk ? "bulk-rename" : `${campaigns[0].id}/rename`}`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...requestContext, accountId, ...(bulk ? { items: campaignRenameItems(items) } : { newName: names[0].trim() }) }),
      });
      const body = await response.json();
      if (!mounted.current) return;
      if (!response.ok) {
        // The route sends only bounded safe messages, never provider transcripts.
        setError(typeof body.error === "string" ? body.error : "Rename unavailable. Inspect before retrying.");
        setResults([]);
        return;
      }
      if (body.accountId !== accountId || !Array.isArray(body.results)
        || body.results.length !== items.length
        || body.results.some((result: CampaignRenameResult, i: number) => result.accountId !== accountId
          || result.campaignId !== items[i].campaignId || result.newName !== items[i].newName.trim()
          || !["acknowledged", "rejected", "ambiguous", "not_sent"].includes(result.outcome))) throw new Error("Invalid reply");
      setResults(body.results);
      onAcknowledged(body.results.filter((result: CampaignRenameResult) => result.outcome === "acknowledged"));
    } catch {
      if (mounted.current) {
        setError("The rename outcome is uncertain. Inspect each campaign in Meta before retrying.");
        setResults([]);
      }
    } finally {
      if (mounted.current) { setPending(false); onPending(false); }
      // This dialog cannot resend an uncertain operation. Close and inspect.
    }
  };
  return <Dialog open onOpenChange={open => { if (!open && !pending) onClose(); }}>
    <DialogContent className="max-h-[85vh] overflow-y-auto">
      <DialogHeader><DialogTitle>{campaigns.length > 1 ? "Rename selected campaigns" : "Rename campaign"}</DialogTitle>
        <DialogDescription>Only campaign names change. Delivery, budgets, saved launches and historical facts stay unchanged. Renames run one at a time without automatic retries.</DialogDescription></DialogHeader>
      {campaigns.map((campaign, index) => <div className="space-y-2 rounded border p-3" key={campaign.id}>
        <p className="break-words text-sm">Current name: {campaign.name}</p>
        <label className="block text-sm" htmlFor={`rename-${campaign.id}`}>New name for {campaign.name}</label>
        <Input id={`rename-${campaign.id}`} value={names[index]} maxLength={400} disabled={review || pending || results !== null}
          onChange={event => setNames(current => current.map((name, i) => i === index ? event.target.value : name))} />
        {review && <p className="break-words text-sm">Save as: {names[index].trim()}</p>}
        {results?.[index] && <p role="status" className="text-sm">{results[index].outcome === "acknowledged" ? "Renamed" : "Not confirmed"}: {results[index].message}</p>}
      </div>)}
      {error && <p role="alert">{error}</p>}
      <DialogFooter>
        <Button variant="outline" disabled={pending} onClick={onClose}>{results !== null ? "Close" : "Cancel"}</Button>
        {results === null && (review ? <>
          <Button variant="outline" disabled={pending} onClick={() => setReview(false)}>Back to editing</Button>
          <Button disabled={pending || !valid} onClick={submit}>{pending ? "Saving…" : campaigns.length > 1 ? "Save campaign names" : "Save name"}</Button>
        </> : <Button disabled={!valid} onClick={() => setReview(true)}>Review rename</Button>)}
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
